// @/panels/AttachmentOptionsPanel.tsx

import React, { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUp, CornerUpLeft, CornerUpRight } from "lucide-react";
import { useDiagram } from "@/context/diagram-context";

const AttachmentOptionsPanel: React.FC = () => {
  const {
    selectedPosition,
    setSelectedPosition,
    selectedAttachmentPoint,
    setSelectedAttachmentPoint,
    availableAttachmentPoints,
    selectedShape,
  } = useDiagram();
  const [filteredAttachmentPoints, setFilteredAttachmentPoints] = useState<
    string[]
  >([]);

  const positionOptions = [
    { value: "top", label: "Top", icon: <ArrowUp size={16} /> },
    {
      value: "front-left",
      label: "Front Left",
      icon: <CornerUpLeft size={16} />,
    },
    {
      value: "front-right",
      label: "Front Right",
      icon: <CornerUpRight size={16} />,
    },
  ];

  useEffect(() => {
    const updateFilteredAttachmentPoints = () => {
      const prefix = `${selectedPosition}-`;
      const newFilteredPoints = availableAttachmentPoints.filter((point) =>
        point.startsWith(prefix)
      );
      setFilteredAttachmentPoints(newFilteredPoints);

      if (
        selectedAttachmentPoint &&
        (newFilteredPoints.length === 0 ||
          !newFilteredPoints.includes(selectedAttachmentPoint))
      ) {
        setSelectedAttachmentPoint("none");
      }
    };
    updateFilteredAttachmentPoints();
  }, [
    availableAttachmentPoints,
    selectedPosition,
    selectedAttachmentPoint,
    setSelectedAttachmentPoint,
  ]);

  const attachmentPointOptions = useMemo(
    () => [
      { value: "none", label: "Default" },
      ...filteredAttachmentPoints.map((point) => ({
        value: point,
        label: point,
      })),
    ],
    [filteredAttachmentPoints]
  );

  const handlePositionChange = (value: string) => {
    setSelectedPosition(value);
    setSelectedAttachmentPoint("none");
  };

  return (
    <div
      className={` absolute z-10 transition-all duration-300 ease-in-out transform ${
        selectedShape
          ? "translate-y-0 opacity-100 pointer-events-auto"
          : "-translate-y-full opacity-0 pointer-events-none"
      }`}
    >
      <div className="bg-surface px-4 py-3 shadow-md border-b border-surface-border">
        <div className="flex items-center gap-2">
          <div className="flex-grow">
            <div className="flex gap-3">
              {positionOptions.map((option) => {
                const isActive = selectedPosition === option.value;
                return (
                  <Button
                    key={option.value}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePositionChange(option.value)}
                    className={`flex items-center gap-2 flex-1 ${
                      isActive
                        ? "!text-[#FFFFFF] !bg-primary border-primary hover:!bg-primary-dark hover:!text-[#FFFFFF]"
                        : "!bg-surface border-surface-border hover:!bg-surface-hover"
                    }`}
                  >
                    {option.icon}
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </div>
          {filteredAttachmentPoints.length > 0 && (
            <div className="w-48">
              <Select
                value={selectedAttachmentPoint || "none"}
                onValueChange={setSelectedAttachmentPoint}
              >
                <SelectTrigger aria-label="Attachment point" className="bg-surface-input border-surface-border text-white">
                  <SelectValue placeholder="Select point" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-surface-border">
                  {attachmentPointOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className="text-white hover:bg-surface-hover"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentOptionsPanel;
